import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AsamcoAuthService } from '@asamco/base';
import { User, UserService } from '@asamco/user-management/shared';
import { pathJoin, skipAuthInterceptor } from '@asamco/utils';
import { BehaviorSubject, combineLatest, from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuditLogData } from '../../interfaces/audit-log-data.interface';
import { Company } from '../../interfaces/company.interface';
import { LinkTemplate } from '../../interfaces/link-template.interface';
import { LinkType } from '../../interfaces/link-type.interface';
import { Link } from '../../interfaces/link.interface';
import { SettingTemplate } from '../../interfaces/setting-template.interface';
import { SelectedCompanyService } from '../../selected-company/selected-company.service';
import { LinkTemplateService } from '../../services/link-template.service';
import { LinkTypeService } from '../../services/link-type.service';
import { LinkService } from '../../services/link.service';
import { SettingTemplateService } from '../../services/setting-template.service';
import { AsyncPipe, DatePipe } from '@angular/common';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxoSelectionModule, DxoFilterRowModule, DxiColumnModule } from 'devextreme-angular/ui/nested';
import { DxDataGridModule } from 'devextreme-angular';

@Component({
    selector: 'app-shell-admin-audit-logs-links-linksettings-updates',
    templateUrl: './admin-audit-logs-links-linksettings-updates.component.html',
    styleUrls: ['./admin-audit-logs-links-linksettings-updates.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        DxDataGridModule,
        DxoSelectionModule,
        DxoFilterRowModule,
        DxiColumnModule,
        DxTemplateModule,
        AsyncPipe,
        DatePipe,
    ],
})
export class AdminAuditLogsLinksLinksettingsUpdatesComponent implements OnInit {
	public audit_logs_companies$: Observable<AuditLogData[]> = new Observable<AuditLogData[]>();

	private userBehaviorSubject: BehaviorSubject<User[]> = new BehaviorSubject<User[]>(null);
	private companyBehaviorSubject: BehaviorSubject<Company[]> = new BehaviorSubject<Company[]>(null);
	private linkTemplateBehaviorSubject: BehaviorSubject<LinkTemplate[]> = new BehaviorSubject<LinkTemplate[]>(null);
	private linkTypeBehaviorSubject: BehaviorSubject<LinkType[]> = new BehaviorSubject<LinkType[]>(null);
	private settingTemplateBehaviourSubject: BehaviorSubject<SettingTemplate[]> = new BehaviorSubject<SettingTemplate[]>(null);
	private linkBehaviourSubject: BehaviorSubject<Link[]> = new BehaviorSubject<Link[]>(null);

	constructor(
		private linkservice: LinkService,
		private settingTemplateService: SettingTemplateService,
		private userService: UserService,
		private companyService: SelectedCompanyService,
		private auth: AsamcoAuthService,
		private linkTemplateService: LinkTemplateService,
		private linkTypeService: LinkTypeService,
		private http: HttpClient
	) {}

	ngOnInit(): void {
		this.audit_logs_companies$ = this.getCompanyAuditData();
		this.retrieveRequiredData();
	}

	retrieveRequiredData() {
		combineLatest([
			this.userService.getAll(),
			this.companyService.getCompanies(),
			this.linkTemplateService.getAllLinkTemplates(),
			this.linkTypeService.getAllLinkTemplates(),
			this.settingTemplateService.getAllSettingTemplates(),
			this.linkservice.getAll(),
		]).subscribe(([users, companies, linkTemplates, linkTypes, settingTemplates, links]) => {
			this.userBehaviorSubject.next(users);
			this.companyBehaviorSubject.next(companies);
			this.linkTemplateBehaviorSubject.next(linkTemplates);
			this.linkTypeBehaviorSubject.next(linkTypes);
			this.settingTemplateBehaviourSubject.next(settingTemplates);
			this.linkBehaviourSubject.next(links);
		});
	}

	getCompanyAuditData(): Observable<AuditLogData[]> {
		let ownParams = new HttpParams();

		ownParams = ownParams.set(
			'$filter',
			`(RecordType eq 'Link' and AuditAction eq 'UPDATE') or (RecordType eq 'LinkSetting' and AuditAction eq 'CREATE')  or (RecordType eq 'LinkSetting' and AuditAction eq 'UPDATE')`
		);

		ownParams = ownParams.set('$select', `timeStamp,userId,userName,auditAction,additionalData, recordType, recordId,companyName`);

		return from(this.auth.getPrimaryTokenAsync()).pipe(switchMap(
			pt => this.http.get(pathJoin([this.auth.userSession.backend.odataBaseUrl, 'dynamic/_AL3_vw_AuditLogData']), {
				headers: skipAuthInterceptor(new HttpHeaders().set('Authorization', 'Bearer ' + pt)),
				params: ownParams,
			})
		),
		// Map to odata value to filter out context
		map((data: any) => {
			return data.value;
		}));
	}

	public convertAdditionalDataToReadableText(auditLogData: AuditLogData): string {
		//1: Convert additionalData to corresponding object based on the RecordType
		switch (auditLogData.recordType) {
			case 'LinkSetting':
				let data = JSON.parse(auditLogData.additionalData);
				let companyName = this.companyBehaviorSubject.value?.find(item => item.id == data?.CompanyId)?.name || '?';
				let userName = this.userBehaviorSubject.value?.find(item => item.id == auditLogData.userId)?.displayName || '?';
				let settingTemplate = this.settingTemplateBehaviourSubject.value?.find(item => item.id == data.SettingTemplateId);
				let link = this.linkBehaviourSubject.value?.find(item => item.id == data.LinkId);
				let fromLinkTemplate = this.linkTemplateBehaviorSubject.value?.find(item => item.id == link?.fromLinkTemplateId);
				let toLinkTemplate = this.linkTemplateBehaviorSubject.value?.find(item => item.id == link?.toLinkTemplateId);
				if (auditLogData.auditAction == 'UPDATE') {
					return `User '${userName}' from company '${auditLogData.companyName}' has changed the setting '${settingTemplate?.displayName || '?'}' to value '${data.SettingsValue}' for link '${fromLinkTemplate?.description || '?'}' '${toLinkTemplate?.description || '?'}' with id '${link.id}'`;
				} else if (auditLogData.auditAction == 'CREATE') {
					return `User '${userName}' from company '${auditLogData.companyName}' has enabled the setting '${settingTemplate?.displayName || '?'}' to value '${data.SettingsValue}' for link '${fromLinkTemplate?.description || '?'}' '${toLinkTemplate?.description || '?'}' with id '${link.id}'`;
				}

			case 'Link':

			//   let data = JSON.parse(auditLogData.additionalData);
			//   let userName = this.userBehaviorSubject.value.find(item => item.id == auditLogData.userId)?.displayName;
			//   let companyName = this.companyBehaviorSubject.value.find(item => item.id == data?.CompanyId)?.name;
			//   let fromLinkTemplate = this.linkTemplateBehaviorSubject.value.find(item => item.id == data?.FromLinkTemplateId);
			//   let toLinkTemplate = this.linkTemplateBehaviorSubject.value.find(item => item.id == data?.ToLinkTemplateId);
			//   let linkType = this.linkTypeBehaviorSubject.value.find(item => item.id == fromLinkTemplate?.linkTypeId)?.description

			//   break;
		}
		return auditLogData.additionalData;
	}
}
